/* eslint-disable complexity */
import React, { useState, useEffect, useMemo, useContext } from 'react';
import {
  TextField,
  LogBehaviourActions,
  TextCollectionAttachmentType,
} from '@scale/shared/types/params/textcollection';
import {
  TextCollectionAnnotation,
  TextFieldAnnotation,
} from '@scale/shared/types/response/textcollection';
import CustomTextField from './CustomTextField';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { FormHelperText, IconButton, Button } from '@material-ui/core';
import { getHelperText } from './helperText';
import {
  getFieldMaxResponses,
  getFieldMinResponses,
  addDefaultResponse,
  getDefaultAnnotation,
} from '@scale/shared/textcollection/defaults';
import classNames from 'classnames';
import { makeStyles } from 'design-system/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { useRLHFHackStore } from 'react-scale/components/scalers/tasks/textcollection/stores/rlhfHackStore';
import _ from 'lodash';
import API from '@scale/frontend-lib/dashboard-api';
import HighlightIssues from 'react-scale/components/scalers/tasks/textcollection/HighlightIssues';
import color from 'color';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useLLMHelper } from 'react-scale/dashboard.scale.com/contexts/LLMHelperContext';
import { relativeLevenshteinDistance } from '@scale/shared/selfServe/helpers';
import {
  DataInfo,
  IOnUploadComplete,
} from 'react-scale/components/scalers/tasks/GenericDataCollection/types';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { ChatTaskBehaviorLogMetadata } from '@scale/shared/types/chat';

interface TextFieldSectionProps {
  field: TextField;
  annotation: TextFieldAnnotation;
  setAnnotation: (annotation: TextFieldAnnotation) => void;
  disabled?: boolean;
  prefillKey?: string;
  setSingleAnnotation: (annotation: TextFieldAnnotation) => void;
  previousTextFieldAnnotations?: TextFieldAnnotation[];
  handleValidateField?: () => void;
  setFieldIdPendingValidation?: (fieldId: string | undefined) => void;
  fieldIdPendingValidation?: string;
  codingFields?: TextCollectionAnnotation[];
  logBehaviourActions?: LogBehaviourActions;
  isTestQuestion?: boolean;
  projectId?: string;
  assignmentId?: string;
  userId?: string;
  chatTaskBehaviorLogMetadata?: ChatTaskBehaviorLogMetadata;
  enableRTLText?: boolean;
}

export default function TextFieldSection(props: TextFieldSectionProps) {
  const {
    fairHackInitialized,
    fairActiveSuggestedFieldIndex,
    fairSetActiveSuggestedFieldIndex,
    fairUpdateSuggestedField,
    fairSuggestedFields,
    fairSuggestedFieldId,
  } = useRLHFHackStore(state => ({
    fairHackInitialized: state.initialized,
    fairSetActiveSuggestedFieldIndex: state.setActiveSuggestedFieldIndex,
    fairUpdateSuggestedField: state.updateSuggestedField,
    fairSuggestedFields: state.suggestedFields,
    fairSuggestedFieldId: state.suggestedFieldId,
    fairActiveSuggestedFieldIndex: state.activeSuggestedFieldIndex,
  }));

  const [textToGPTValidate, setTextToGPTValidate] = useState<any>(undefined); // should be a string
  const [gptTextErrors, setGptTextErrors] = useState<any>(undefined);
  const [isGptLoading, setIsGptLoading] = useState(false);
  const llmHelper = useLLMHelper();
  const [aiAssistantLoading, setAiAssistantLoading] = useState<boolean>(false);
  const [aiAssistantEval, setAiAssistantEval] = useState<string>('');
  const [aiAssistantEvalOriginal, setAiAssistantEvalOriginal] = useState<string>('');
  const chatTaskContext = useContext(ChatTaskContext);

  useEffect(() => {
    if (fairHackInitialized && props.annotation) {
      const content = _.get(props.annotation, 'response[0]', '');
      setTextToGPTValidate(content);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fairHackInitialized, props.annotation, props.field.field_id]);

  useEffect(() => {
    if (
      llmHelper.aiAssistantEnabled &&
      !llmHelper.attemptTexts[props.field.field_id] &&
      props.annotation
    ) {
      const attempt = _.get(props.annotation, 'response[0]', '').length
        ? _.get(props.annotation, 'response[0]', '')
        : 'Write here...';
      llmHelper.setAttemptTexts(p => ({
        ...p,
        [props.field.field_id]: attempt,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.annotation,
    llmHelper.aiAssistantEnabled,
    llmHelper.attemptTexts,
    props.field.field_id,
  ]);

  const getGPTTextErrors = async () => {
    setIsGptLoading(true);
    try {
      const prompt =
        'Below is some text; can you point to specific phrases that have potential inaccuracies,' +
        ' or potential problems for spelling/grammar? Be nitpicky. Please return in a json response, in the same order as in the original text, such as' +
        ' [{type: "potential_inaccuracy", text: "specific quote from the text. be sure to retain the original capitalization, spelling, and punctuation", ' +
        'why: "specific information about why this fragment might be inaccurate. be specific"}, {type: "spellinggrammar", ' +
        'text: "specific block of at least 3 words from the text. be sure to retain the original capitalization, spelling, and punctuation", ' +
        'why: "why is this a grammatical error? be specific"}]';
      const { content } = await API.selfserve.askGPT({
        promptSystem: prompt,
        promptUser: textToGPTValidate ?? '',
        options: { model: 'gpt-3.5-turbo' },
      });

      const contentJSON = JSON.parse(content);
      setGptTextErrors(contentJSON);
    } catch (e) {
      // Do nothing
    } finally {
      setIsGptLoading(false);
    }
  };

  useEffect(() => {
    if (
      llmHelper.aiAssistantEnabled &&
      Object.keys(llmHelper.aiAssistantPrompts).includes(props.field.field_id)
    ) {
      (async () => {
        try {
          setAiAssistantLoading(true);
          const content = await llmHelper.getAiAssistantResponse(props.field.field_id);
          if (content && llmHelper.aiAssistantPrompts[props.field.field_id]?.isResponseOption)
            setAiAssistantEvalOriginal(content);
          if (content) setAiAssistantEval(content);
        } finally {
          setAiAssistantLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [llmHelper.aiAssistantEnabled, llmHelper.aiAssistantPrompts, props.field.field_id]);

  const styles = useTextFieldSectionStyles(props);
  const autoFocusIndex = React.useRef<number>(-1);
  const isFirstUpdate = React.useRef(true);
  const addNewResponse = () => {
    autoFocusIndex.current = props.annotation.response.length;
    props.setAnnotation(addDefaultResponse(props.annotation, props.field) as TextFieldAnnotation);
  };
  const onChange = function (index: number, value: string, skipAiAssistant?: boolean): void {
    autoFocusIndex.current = -1;
    const response = props.annotation.response.slice();
    response[index] = value;
    const annotation: TextFieldAnnotation = {
      ...props.annotation,
      response,
    };
    if (props.handleValidateField && props.fieldIdPendingValidation !== props.field.field_id) {
      props.setFieldIdPendingValidation?.(props.field.field_id);
    }
    props.setAnnotation(annotation);
    if (fairHackInitialized && props.field.field_id === fairSuggestedFieldId) {
      fairUpdateSuggestedField(value);
    }
    if (llmHelper.aiAssistantEnabled && !skipAiAssistant) {
      if (llmHelper.activeIndices[props.field.field_id]) {
        setAiAssistantEval(value);
      } else {
        llmHelper.setAttemptTexts(p => ({
          ...p,
          [props.field.field_id]: value,
        }));
      }
    }
    if (llmHelper.aiAssistantEnabled) {
      (async () => {
        const distance = relativeLevenshteinDistance(aiAssistantEvalOriginal, value);
        if (!isNaN(distance) && distance < 0.1) {
          llmHelper.setResponseNotDifferentEnough(p => [...new Set([...p, props.field.field_id])]);
        } else {
          llmHelper.setResponseNotDifferentEnough(p => p.filter(r => r !== props.field.field_id));
        }
      })();
    }
  };
  const onRemove = (index: number) => {
    autoFocusIndex.current = -1;
    const response = props.annotation.response.slice();
    response.splice(index, 1);
    const annotation: TextFieldAnnotation = {
      ...props.annotation,
      response,
    };
    props.setAnnotation(annotation);
  };

  const onPrefill = function (value: string): void {
    autoFocusIndex.current = -1;
    const response = props.annotation.response.slice();
    response[0] = value;
    const annotation: TextFieldAnnotation = {
      ...props.annotation,
      response,
    };
    props.setSingleAnnotation(annotation);
  };

  const [fieldPrefillMap, setFieldPrefillMap] = React.useState<
    Record<string, Record<string, string>>
  >({});
  React.useEffect(() => {
    setFieldPrefillMap({});
    if (!props.field.prefill?.mapS3Artifact) {
      return;
    }
    const fetchPrefillMap = async () => {
      try {
        const resp = await fetch(props.field.prefill?.mapS3Artifact!);
        const respText = await resp.text();
        const prefillMap = JSON.parse(respText);
        setFieldPrefillMap(prefillMap);
      } catch (error) {
        return;
      }
    };
    fetchPrefillMap();
  }, [props.field]);

  const [additionalDescription, setAdditionalDescription] = React.useState<string>('');
  React.useEffect(() => {
    if (props.prefillKey && !_.isEmpty(fieldPrefillMap)) {
      if (isFirstUpdate.current) {
        isFirstUpdate.current = false;
        if (!_.isEqual(props.annotation, getDefaultAnnotation(props.field))) {
          return;
        }
      }
      const prefillValue =
        props.prefillKey in fieldPrefillMap &&
        props.field.field_id in fieldPrefillMap[props.prefillKey]
          ? fieldPrefillMap[props.prefillKey][props.field.field_id]
          : '';
      if (props.field.prefill?.addToDescription) {
        setAdditionalDescription(prefillValue);
      } else {
        onPrefill(prefillValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.prefillKey, fieldPrefillMap, props.field]);

  const [blurred, setBlurred] = React.useState(false);

  const onBlur = () => {
    setBlurred(true);
  };
  const responses = props.annotation.response.length;
  const minResponses = getFieldMinResponses(props.field);
  const maxResponses = getFieldMaxResponses(props.field);
  const isMultiple = maxResponses > 1;
  const codeFieldsSourceCode =
    props.codingFields && props.codingFields.length > 0
      ? props.codingFields.map((c: any) =>
          c.response && c.response.length > 0 && _.has(c.response[0], 'sourceCode')
            ? c.response[0].sourceCode
            : '',
        )
      : undefined;

  const [fileInfo, setFileInfo] = useState<DataInfo>({
    unsignedS3DataUrl: '',
    unsignedDataUrl: '',
    fileId: '',
    contentType: '',
    dataChunkInfo: [undefined],
  });
  const setSignedDataUrl = (signedDataUrl: string) =>
    setFileInfo(fileInfo => ({ ...fileInfo, signedDataUrl }));
  const onUploadComplete = (payload: IOnUploadComplete) => {
    setFileInfo(_.omit(payload, ['dataIndex', 'dataChunkIndex']));
  };
  const [progress, setUploadProgress] = useState(0);
  useEffect(() => {
    const fileUrl = fileInfo.unsignedDataUrl || fileInfo.unsignedS3DataUrl || '';
    fileUrl &&
      props.setAnnotation({
        ...props.annotation,
        response: [fileUrl],
      });
  }, [fileInfo, props.setAnnotation]);
  const fileUploadProps = useMemo(
    () =>
      props.field.can_upload_file
        ? {
            projectId: props.projectId,
            dataToCollect: {
              name: props.field.field_id,
              type: TextCollectionAttachmentType.Text,
            },
            userId: props.userId,
            assignmentId: props.assignmentId,
            setSignedDataUrl,
            setFileInfo,
            fileInfo,
            uploadProgress: progress,
            setUploadProgress,
            onUploadComplete,
            fileIndex: 0,
          }
        : undefined,
    [
      props.field.can_upload_file,
      props.field.field_id,
      props.projectId,
      props.userId,
      props.assignmentId,
      fileInfo,
      progress,
    ],
  );
  const textFields = props.annotation.response.map((rawResponse: string, index: number) => {
    const response = ensureString(rawResponse);
    const isFirst = index === 0;
    const isLast = index === props.annotation.response.length - 1;
    const isBlank = !response.trim();
    let errorText;

    if (blurred && props.field.min_characters && response.length < props.field.min_characters) {
      errorText = `This field must be at least ${props.field.min_characters} characters long`;
    }

    if (blurred && props.field.required && isBlank) {
      errorText = 'This field cannot be empty';
    }

    if (blurred && props.field.max_characters && response.length > props.field.max_characters) {
      errorText = `This field must be at most ${props.field.max_characters} characters long`;
    }
    const requiredStr = props.field.required ? ' *' : '';
    const title = `${props.field.title}${requiredStr}`;
    const combinedDescription = `${props.field.description ?? ''}${additionalDescription}`;
    const showWordCounter =
      props.field.show_word_counter ||
      props.field.field_id === 'final_fix_modification' || // fair hack
      (chatTaskContext.enableWordCounter ?? false);

    return (
      <div
        key={`${props.field.field_id} ${index}`}
        className={classNames({
          [styles.multiple]: isMultiple,
          [styles.first]: isFirst,
          [styles.last]: isLast,
        })}
      >
        <CustomTextField
          isTestQuestion={props.isTestQuestion}
          fieldId={props.field.field_id}
          classes={{ root: styles.textField }}
          autoFocus={autoFocusIndex.current === index}
          minChars={props.field.min_characters}
          maxChars={props.field.max_characters}
          minTokens={props.field.min_tokens}
          maxTokens={props.field.max_tokens}
          index={index}
          value={response}
          setIndexValue={onChange}
          title={title}
          description={
            !isMultiple && combinedDescription.length > 0 ? combinedDescription : undefined
          }
          disabled={props.disabled}
          hint={props.field.hint || props.field.instruction}
          shouldAutoComplete={true}
          onBlur={onBlur}
          errorText={errorText}
          spellcheck={props.field.spellcheck}
          useSaplingGrammarCheck={props.field.use_grammar_check}
          showWordCounter={showWordCounter}
          flexibleMinRows={props.field.flexible_min_rows}
          disablePasting={props.field.disable_pasting}
          showLatexPreview={props.field.show_latex_preview}
          showMarkdownPreview={props.field.show_markdown_preview}
          showDiff={props.field.show_diff}
          showEquationBuilder={props.field.show_equation_builder}
          codingFields={codeFieldsSourceCode}
          logBehaviourActions={props.logBehaviourActions}
          chatTaskBehaviorLogMetadata={props.chatTaskBehaviorLogMetadata}
          fileUploadProps={fileUploadProps}
          disableCopying={props.field.disable_copying}
          defaultText={props.field.default_text}
          enableRTLText={props.enableRTLText}
        />
        {props.previousTextFieldAnnotations && (
          <div className={styles.previousResponsesSection}>
            <div className={styles.previousResponsesSubheaderText}>
              {'See the previous responses for this prompt below'}
            </div>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableBody>
                  {props.previousTextFieldAnnotations.map((annotation, index) => (
                    <TableRow key={index}>
                      <TableCell size="small">{_.first(annotation?.response)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}
        {props.fieldIdPendingValidation === props.field.field_id && (
          <div className={styles.actions}>
            <Button
              type="button"
              classes={{ root: styles.addButton, label: styles.addButtonText }}
              color="primary"
              variant="text"
              onClick={props.handleValidateField}
            >
              Validate {props.field.title}
            </Button>
          </div>
        )}
        {isMultiple && !props.disabled && (
          <div className={styles.actions}>
            <IconButton
              component="div"
              classes={{ root: styles.delete }}
              onClick={() => onRemove(index)}
              disabled={index < minResponses}
            >
              <TrashIcon />
            </IconButton>
          </div>
        )}
      </div>
    );
  });

  const renderFairHackComponent =
    fairHackInitialized && props.field.field_id === fairSuggestedFieldId ? (
      <>
        <p>
          <b>
            Please use these versions to help you get started with writing a better response! Your
            progress will be saved if you switch between them but note that only the one you select
            at the very end will be selected as the final response.
          </b>
        </p>
        {fairSuggestedFields.map((suggestedField, index) => {
          return (
            <Button
              disabled={props.disabled}
              variant="contained"
              style={{
                color: index === fairActiveSuggestedFieldIndex ? '#2B99D8' : undefined,
                border: index === fairActiveSuggestedFieldIndex ? '1px solid #2B99D8' : undefined,
                marginRight: '2px',
                cursor: 'pointer',
              }}
              key={index}
              onClick={() => {
                fairSetActiveSuggestedFieldIndex(index);
              }}
            >
              V{`${index + 1}`}
            </Button>
          );
        })}

        {textToGPTValidate && (
          <Button
            type="button"
            variant="contained"
            classes={{ root: styles.continueButton, contained: styles.continueButton }}
            onClick={() => {
              getGPTTextErrors();
            }}
            disabled={isGptLoading}
          >
            Check For Errors!
          </Button>
        )}
        {isGptLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </div>
        ) : (
          textToGPTValidate &&
          gptTextErrors && <HighlightIssues text={textToGPTValidate} errors={gptTextErrors} />
        )}
      </>
    ) : null;

  const renderAiAssistantComponent = llmHelper.aiAssistantEnabled ? (
    llmHelper.aiAssistantPrompts[props.field.field_id]?.isResponseOption ? (
      <>
        <p style={{ lineHeight: 1.4, fontWeight: 500 }}>
          Please use option 2 to help you write a better response! Note that only the one you select
          at the very end will be selected as the final response.
        </p>
        <Button
          disabled={props.disabled}
          variant="contained"
          style={{
            color: !llmHelper.activeIndices[props.field.field_id] ? '#2B99D8' : undefined,
            border: !llmHelper.activeIndices[props.field.field_id]
              ? '1px solid #2B99D8'
              : undefined,
            marginRight: 8,
            cursor: 'pointer',
            textTransform: 'none',
          }}
          onClick={() => {
            llmHelper.setActiveIndices(p => ({ ...p, [props.field.field_id]: false }));
            onChange(0, llmHelper.attemptTexts[props.field.field_id], true);
          }}
        >
          Option 1
        </Button>
        <Button
          disabled={props.disabled || aiAssistantLoading}
          variant="contained"
          style={{
            color: llmHelper.activeIndices[props.field.field_id] ? '#2B99D8' : undefined,
            border: llmHelper.activeIndices[props.field.field_id] ? '1px solid #2B99D8' : undefined,
            cursor: 'pointer',
            textTransform: 'none',
          }}
          onClick={() => {
            llmHelper.setActiveIndices(p => ({ ...p, [props.field.field_id]: true }));
            onChange(0, aiAssistantEval, true);
          }}
        >
          Option 2
          {aiAssistantLoading && (
            <div style={{ marginLeft: 8 }}>
              <CircularProgress size={12} />
            </div>
          )}
        </Button>
      </>
    ) : !!aiAssistantEval.length || aiAssistantLoading ? (
      <div
        style={{
          marginBottom: 8,
          padding: 8,
          whiteSpace: 'pre-wrap',
          backgroundColor: 'rgba(144, 202, 249, .2)',
          borderRadius: 8,
        }}
      >
        {aiAssistantLoading && !aiAssistantEval.length
          ? 'AI assistant evaluation is loading...'
          : `The AI assistant suggests: ${aiAssistantEval}`}
      </div>
    ) : null
  ) : null;
  return (
    <>
      {isMultiple && props.field.description && (
        <FormHelperText classes={{ root: styles.helperText }}>
          {getHelperText({ description: props.field.description })}
        </FormHelperText>
      )}
      {renderFairHackComponent}
      {renderAiAssistantComponent}
      {textFields}
      {responses < maxResponses && (
        <div style={{ display: props.disabled ? 'none' : 'block' }}>
          <Button
            type="button"
            classes={{ root: styles.addButton, label: styles.addButtonText }}
            startIcon={<Plus />}
            color="primary"
            variant="text"
            onClick={addNewResponse}
          >
            Add {props.field.title}
          </Button>
        </div>
      )}
    </>
  );
}

const ensureString = (value: string | number): string => {
  if (typeof value !== 'string') {
    return value?.toString() ?? '';
  }
  return value;
};
const darken = (themeColor: string) => color(themeColor).darken(0.1).toString();

const useTextFieldSectionStyles = makeStyles(theme => {
  return {
    helperText: {
      marginBottom: 16,
      fontSize: '14px',
      lineHeight: '20px',
      fontWeight: 500,
      color: theme.colors.black,
    },
    first: {},
    last: {},
    multiple: {
      marginBottom: 16,
      display: 'flex',
      flexDirection: 'row',
    },
    actions: {
      paddingTop: 8,
      paddingLeft: 8,
    },
    delete: {
      color: `rgba(0, 0, 0, 0.5)`,
      padding: 10,
      '& span': {
        padding: '0 1px',
      },
    },
    textField: {
      flex: 1,
    },
    addButton: {
      marginRight: 8,
      marginBottom: 8,
    },
    addButtonText: {
      color: theme.colors.blue,
      fontWeight: 600,
      fontSize: '13px',
      lineHeight: '20px',
      letterSpacing: '0.1px',
    },
    previousResponsesSection: {
      marginTop: 14,
      marginLeft: 14,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    },
    previousResponsesSubheaderText: {
      fontSize: '12px',
      lineHeight: '20px',
    },
    continueButton: {
      background: '#6A5DF9',
      color: theme.colors.white,
      '&:hover, &:focus': {
        background: darken('#6A5DF9'),
      },
      fontWeight: 600,
      fontSize: '13px',
      lineHeight: '20px',
      letterSpacing: '0.1px',
      textTransform: 'none',
      margin: '15px',
    },
  };
});

const TrashIcon = () => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M13.2 2.39961H10.2L9.96503 1.93211C9.91524 1.83217 9.83856 1.74809 9.74361 1.68935C9.64865 1.63061 9.53918 1.59953 9.42753 1.59961H6.57002C6.45862 1.59919 6.34935 1.63015 6.25473 1.68895C6.16011 1.74776 6.08396 1.83203 6.03502 1.93211L5.80002 2.39961H2.80002C2.69394 2.39961 2.5922 2.44176 2.51718 2.51677C2.44217 2.59179 2.40002 2.69353 2.40002 2.79961V3.59961C2.40002 3.7057 2.44217 3.80744 2.51718 3.88246C2.5922 3.95747 2.69394 3.99961 2.80002 3.99961H13.2C13.3061 3.99961 13.4079 3.95747 13.4829 3.88246C13.5579 3.80744 13.6 3.7057 13.6 3.59961V2.79961C13.6 2.69353 13.5579 2.59179 13.4829 2.51677C13.4079 2.44176 13.3061 2.39961 13.2 2.39961ZM3.73002 13.2746C3.7491 13.5793 3.88356 13.8652 4.10604 14.0742C4.32851 14.2832 4.62227 14.3996 4.92752 14.3996H11.0725C11.3778 14.3996 11.6715 14.2832 11.894 14.0742C12.1165 13.8652 12.2509 13.5793 12.27 13.2746L12.8 4.79961H3.20002L3.73002 13.2746Z" />
    </svg>
  );
};

function Plus() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.8 6.80039H9.20003V3.20039C9.20003 2.75864 8.84177 2.40039 8.40002 2.40039H7.60002C7.15827 2.40039 6.80002 2.75864 6.80002 3.20039V6.80039H3.20002C2.75827 6.80039 2.40002 7.15864 2.40002 7.60039V8.40039C2.40002 8.84214 2.75827 9.20039 3.20002 9.20039H6.80002V12.8004C6.80002 13.2421 7.15827 13.6004 7.60002 13.6004H8.40002C8.84177 13.6004 9.20003 13.2421 9.20003 12.8004V9.20039H12.8C13.2418 9.20039 13.6 8.84214 13.6 8.40039V7.60039C13.6 7.15864 13.2418 6.80039 12.8 6.80039Z"
        fill="#2B99D8"
      />
    </svg>
  );
}
